import React, { useState } from 'react'
import {
	Button,
	Form,
	Modal,
} from 'react-bootstrap'
import Select from 'react-select'
import { useAtomValue, useSetAtom } from 'jotai'
import dt from 'dayjs'
import { useRoleList } from '../../hooks'
import * as lib from '../../lib'
import * as state from '../../states'
import {
	IFetchItemsSetup,
	IRole,
} from '../../types'
import { ISelectOption } from '../../types/ui'
import { UiTogglePassword, requiredFieldSign } from '../ui'
import { reactSelectStyles } from '../filters'
import { ALL_LANG_KEY } from '../../types/lang'
import { useTranslation } from 'react-i18next'
import { CheckboxBox, CheckboxBoxes } from '../revenueAgency'

type IAccountCreateModalProps = {
	show: boolean;
	onClose: () => void;
	onSave: () => void;
}

export const AccountCreateModal = (
	props: IAccountCreateModalProps
) => {
	const { t } = useTranslation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const { show, onClose, onSave } = props

	const setup: IFetchItemsSetup = {
		filter: [],
		sort: [],
		skip: 0,
		take: 'all',
	}
	const {
		error: roleError,
		items: roleItems,
		total: roleAmount,
		warmUp: roleWarmUp,
	} = useRoleList(setup, true)
	const roleOptions: ISelectOption<number>[] = roleItems
		.map((role: IRole) => {
			return {
				value: role.id,
				label: `${role.id}. ${role.name} (${role.code})`,
			}
		})

	const [email, setEmail] = useState<string>('')
	const [isActive, setIsActive] = useState<boolean>(false)
	const [isFired, setIsFired] = useState<boolean>(false)
	const [roleId, setRoleId] = useState<number | null>(null)
	const [registeredAt, setRegisteredAt] = useState<Date | null | undefined>(undefined)
	const [login, setLogin] = useState<string>('')
	const [password, setPassword] = useState<string>('')
	const [keitaroLogin, setKeitaroLogin] = useState<string>('')

	const [showPassword, setShowPassword] = useState<boolean>(false)

	if (!show) {
		return null
	}

	const _getAccountRequestBody = () => {
		return JSON.stringify({
			email,
			isActive,
			isFired,
			roleId,
			registeredAt: registeredAt
				? dt(registeredAt).toISOString()
				: dt().toISOString(),
			extra: {
				keitaroLogin
			},
			auth: {
				...login && { login },
				...password && { password },
			}
		})
	}

	const onCreate = async () => {
		const uri: string = `account`
		const method: string = 'POST'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)
		const body: string = _getAccountRequestBody()
		const options: lib.FetchOptions = {
			body,
			method,
			params,
			headers,
		}

		try {
			setFetchIsLoading(state.turnOnLoader)
			const created = await lib.fetching<{ id: number }>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const readyToSend = !!email && !!roleId && !!login && !!password
	const modalTitle = t(ALL_LANG_KEY.account_crud_modal.title_create)

	return <>
		<Modal show={show} onHide={onClose} className={'scroll'}>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form>
					<Form.Group>

						<Form.Label className="text-dark" htmlFor={'input-email'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_email_label)}`}{requiredFieldSign}</Form.Label>
						<Form.Control
							id={'input-email'}
							autoComplete={'off'}
							placeholder={`${t(ALL_LANG_KEY.account_crud_modal.field_email_placeholder)}`}
							type="text"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>

						<CheckboxBoxes>
							<CheckboxBox>
								<Form.Label className="text-dark" htmlFor={'input-is-active'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_is_active_label)}`}</Form.Label>
								<Form.Check
									checked={isActive ?? false}
									id={'input-is-active'}
									type="checkbox"
									onChange={(e) => setIsActive(e.target.checked)}
								/>
							</CheckboxBox>

							<CheckboxBox>
								<Form.Label className="text-dark" htmlFor={'input-is-fired'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_is_fired_label)}`}</Form.Label>
								<Form.Check
									checked={isFired ?? false}
									id={'input-is-fired'}
									type="checkbox"
									onChange={(e) => setIsFired(e.target.checked)}
								/>
							</CheckboxBox>
						</CheckboxBoxes>

						<Form.Label className="text-dark" htmlFor={'select-role-id'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_role_label)}`}{requiredFieldSign}</Form.Label>
						<Select
							inputId={'select-role-id'}
							className="react-select-container"
							options={roleOptions}
							value={roleOptions.find((role) => role.value === roleId) ?? null}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							isClearable={true}
							isSearchable={false}
							styles={reactSelectStyles<ISelectOption<number>, false>(isThemeDark)}
							onChange={(selectedItem) => {
								setRoleId(selectedItem?.value ?? null)
							}}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-login'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_login_label)}`}{requiredFieldSign}</Form.Label>
						<Form.Control
							id={'input-login'}
							autoComplete={'off'}
							placeholder={`${t(ALL_LANG_KEY.account_crud_modal.field_login_placeholder)}`}
							type="text"
							value={login}
							onChange={(e) => setLogin(e.target.value)}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-password'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_password_label)}`}{requiredFieldSign}</Form.Label>
						<div className="position-relative">
							<Form.Control
								id={'input-password'}
								autoComplete={'off'}
								placeholder={`${t(ALL_LANG_KEY.account_crud_modal.field_password_placeholder)}`}
								type={!showPassword ? 'password' : 'text'}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
							<UiTogglePassword
								showPassword={showPassword}
								setShowPassword={setShowPassword}
							/>
						</div>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-login-kt'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_kt_group_label)}`}</Form.Label>
						<Form.Control
							id={'input-login-kt'}
							autoComplete={'off'}
							placeholder={`${t(ALL_LANG_KEY.account_crud_modal.field_kt_group_placeholder)}`}
							type="text"
							value={keitaroLogin}
							onChange={(e) => setKeitaroLogin(e.target.value)}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-registered-at'}>{`${t(ALL_LANG_KEY.account_crud_modal.field_date_registered_label)}`}</Form.Label>
						<Form.Control
							id={'input-registered-at'}
							placeholder={`${t(ALL_LANG_KEY.account_crud_modal.field_date_registered_placeholder)}`}
							type="date"
							value={dt(registeredAt).isValid() ? dt.utc(registeredAt).format('YYYY-MM-DD') : ''}
							onChange={(e) => {
								const value = e.target.value
								const v = dt.utc(value)
								setRegisteredAt(v.isValid() ? v.toDate() : null)
							}}
						/>
						<br/>

					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" disabled={!readyToSend} onClick={onCreate}>{`${t(ALL_LANG_KEY.account_crud_modal.btn_create)}`}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.account_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
